import React, { useState, useRef } from 'react'
import { ProfileCustomization, UserProfile } from '../types'

interface ProfileCustomizationProps {
  profile: UserProfile
  onSave: (customization: Partial<UserProfile>) => void
  onCancel: () => void
}

const ProfileCustomizationComponent: React.FC<ProfileCustomizationProps> = ({
  profile,
  onSave,
  onCancel
}) => {
  const [customization, setCustomization] = useState(profile.customization)
  const [avatarType, setAvatarType] = useState(profile.avatarType)
  const [coverPhoto, setCoverPhoto] = useState(profile.coverPhoto || '')
  const [customAvatarUrl, setCustomAvatarUrl] = useState(profile.customAvatarUrl || '')
  const [bio, setBio] = useState(profile.bio || '')
  const [bioMarkdown, setBioMarkdown] = useState(profile.bioMarkdown || '')
  const [displayName, setDisplayName] = useState(profile.displayName || '')
  const [location, setLocation] = useState(profile.location || '')
  
  const avatarUploadRef = useRef<HTMLInputElement>(null)
  const coverUploadRef = useRef<HTMLInputElement>(null)

  const themes = [
    { id: 'default', name: 'Default', colors: ['#3b82f6', '#e5e7eb'] },
    { id: 'dark', name: 'Dark Mode', colors: ['#1f2937', '#374151'] },
    { id: 'nature', name: 'Nature', colors: ['#10b981', '#065f46'] },
    { id: 'minimal', name: 'Minimal', colors: ['#6b7280', '#f9fafb'] },
    { id: 'vibrant', name: 'Vibrant', colors: ['#8b5cf6', '#f59e0b'] },
    { id: 'professional', name: 'Professional', colors: ['#1e40af', '#64748b'] }
  ]

  const layoutStyles = [
    { id: 'classic', name: 'Classic', description: 'Traditional layout with sidebar' },
    { id: 'modern', name: 'Modern', description: 'Clean, centered design' },
    { id: 'compact', name: 'Compact', description: 'Space-efficient layout' },
    { id: 'detailed', name: 'Detailed', description: 'Comprehensive information display' }
  ]

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setCustomAvatarUrl(e.target?.result as string)
        setAvatarType('upload')
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCoverUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setCoverPhoto(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const generateAvatar = () => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff']
    const randomColor = colors[Math.floor(Math.random() * colors.length)]
    const initials = (profile.displayName || profile.username || 'U').substring(0, 2).toUpperCase()
    
    const svg = `
      <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
        <rect width="100" height="100" fill="${randomColor}"/>
        <text x="50%" y="50%" text-anchor="middle" dy="0.3em" font-family="Arial" font-size="40" fill="white">${initials}</text>
      </svg>
    `
    
    const encodedSvg = 'data:image/svg+xml;base64,' + btoa(svg)
    setCustomAvatarUrl(encodedSvg)
    setAvatarType('generated')
  }

  const handleSave = () => {
    onSave({
      customization,
      avatarType,
      coverPhoto,
      customAvatarUrl,
      bio,
      bioMarkdown,
      displayName,
      location,
      preferences: {
        ...profile.preferences,
        profileTheme: customization.theme
      }
    })
  }

  return (
    <div className="profile-customization-modal">
      <div className="modal-overlay" onClick={onCancel}></div>
      <div className="modal-content">
        <div className="modal-header">
          <h2>🎨 Customize Your Profile</h2>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        <div className="customization-tabs">
          <div className="tab-content">
            {/* Basic Information */}
            <div className="section">
              <h3>📝 Basic Information</h3>
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your display name"
                />
              </div>
              
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Your location"
                />
              </div>
              
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell others about yourself..."
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Bio (Markdown) <span className="optional">Optional</span></label>
                <textarea
                  value={bioMarkdown}
                  onChange={(e) => setBioMarkdown(e.target.value)}
                  placeholder="Enhanced bio with markdown formatting..."
                  rows={4}
                />
                <small>Supports **bold**, *italic*, [links](url), and more</small>
              </div>
            </div>

            {/* Avatar Customization */}
            <div className="section">
              <h3>🖼️ Avatar & Cover Photo</h3>
              
              <div className="avatar-options">
                <div className="current-avatar">
                  {avatarType === 'emoji' && <span className="emoji-avatar">{profile.avatar}</span>}
                  {avatarType === 'upload' && customAvatarUrl && <img src={customAvatarUrl} alt="Avatar" />}
                  {avatarType === 'generated' && customAvatarUrl && <img src={customAvatarUrl} alt="Avatar" />}
                </div>
                
                <div className="avatar-controls">
                  <button
                    className={avatarType === 'emoji' ? 'active' : ''}
                    onClick={() => setAvatarType('emoji')}
                  >
                    😀 Emoji
                  </button>
                  
                  <button
                    className={avatarType === 'upload' ? 'active' : ''}
                    onClick={() => avatarUploadRef.current?.click()}
                  >
                    📤 Upload
                  </button>
                  
                  <button
                    className={avatarType === 'generated' ? 'active' : ''}
                    onClick={generateAvatar}
                  >
                    🎲 Generate
                  </button>
                </div>
                
                <input
                  ref={avatarUploadRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="cover-photo-section">
                <label>Cover Photo</label>
                {coverPhoto && (
                  <div className="cover-preview">
                    <img src={coverPhoto} alt="Cover" />
                  </div>
                )}
                <button onClick={() => coverUploadRef.current?.click()}>
                  {coverPhoto ? '🔄 Change Cover' : '📷 Add Cover Photo'}
                </button>
                <input
                  ref={coverUploadRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {/* Theme Selection */}
            <div className="section">
              <h3>🎨 Theme & Colors</h3>
              <div className="theme-grid">
                {themes.map(theme => (
                  <div
                    key={theme.id}
                    className={`theme-option ${customization.theme === theme.id ? 'selected' : ''}`}
                    onClick={() => setCustomization(prev => ({ ...prev, theme: theme.id as any }))}
                  >
                    <div className="theme-preview">
                      <div 
                        className="color-primary" 
                        style={{ backgroundColor: theme.colors[0] }}
                      ></div>
                      <div 
                        className="color-secondary" 
                        style={{ backgroundColor: theme.colors[1] }}
                      ></div>
                    </div>
                    <span>{theme.name}</span>
                  </div>
                ))}
              </div>
              
              <div className="color-picker">
                <label>Accent Color</label>
                <input
                  type="color"
                  value={customization.accentColor}
                  onChange={(e) => setCustomization(prev => ({ ...prev, accentColor: e.target.value }))}
                />
              </div>
            </div>

            {/* Layout Options */}
            <div className="section">
              <h3>📐 Layout Style</h3>
              <div className="layout-options">
                {layoutStyles.map(layout => (
                  <div
                    key={layout.id}
                    className={`layout-option ${customization.layoutStyle === layout.id ? 'selected' : ''}`}
                    onClick={() => setCustomization(prev => ({ ...prev, layoutStyle: layout.id as any }))}
                  >
                    <div className="layout-preview">
                      <div className={`preview-${layout.id}`}></div>
                    </div>
                    <div className="layout-info">
                      <strong>{layout.name}</strong>
                      <small>{layout.description}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Display Options */}
            <div className="section">
              <h3>👁️ Display Options</h3>
              <div className="toggle-options">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={customization.showBadges}
                    onChange={(e) => setCustomization(prev => ({ ...prev, showBadges: e.target.checked }))}
                  />
                  <span>Show Badges</span>
                </label>
                
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={customization.showStats}
                    onChange={(e) => setCustomization(prev => ({ ...prev, showStats: e.target.checked }))}
                  />
                  <span>Show Statistics</span>
                </label>
                
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={customization.showActivity}
                    onChange={(e) => setCustomization(prev => ({ ...prev, showActivity: e.target.checked }))}
                  />
                  <span>Show Activity Timeline</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave}>
            💾 Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProfileCustomizationComponent